import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const COMPANY = "RIMITANA GLOBAL NETWORKS PRIVATE LIMITED";
const EMAIL = "rimitananetwork@gmail.com";
const PHONE = "+91 9690 181 181";
const SITE = "www.animalmela.com";

const SECTIONS = [
  {
    h: "Introduction",
    p: [
      `This Refund & Cancellation Policy explains how subscription payments, cancellations and refunds are handled for the AnimalMela platform ("Service"), operated by ${COMPANY} ("we", "our", "us").`,
      "This policy forms part of our Terms & Conditions. By subscribing to or using the Service, you agree to the terms below.",
    ],
  },
  {
    h: "Subscription Payments",
    list: [
      "AnimalMela is offered to Pashu Haat owners on paid subscription plans (e.g. Starter, Professional, Enterprise), billed in advance for each billing period.",
      "Current plans and prices are displayed on our website and may be revised with prior notice.",
      "Applicable taxes (including GST) may be added to the listed prices.",
      "Subscription fees are generally non-refundable once the service has been activated for the billing period.",
    ],
  },
  {
    h: "Monthly Subscription Policy",
    list: [
      "Monthly plans renew every month on the billing date.",
      "You may cancel at any time; the cancellation takes effect at the end of the current monthly period.",
      "Fees for a monthly period that has already started are non-refundable, and no pro-rata refunds are issued for unused days.",
    ],
  },
  {
    h: "Annual Subscription Policy",
    list: [
      "Annual plans are billed once for the full year and renew on the anniversary of the billing date.",
      "Annual plans may be cancelled at any time before the next renewal date to stop the upcoming renewal.",
      "Fees for an annual period that has already started are non-refundable, except where expressly required by applicable law or agreed by us in writing.",
    ],
  },
  {
    h: "Setup & Training Charges",
    p: [
      "Setup, onboarding, training, customization and implementation charges (where applicable) are one-time service charges. These are non-refundable once the service has been delivered, in full or in part.",
    ],
  },
  {
    h: "Add-on Services (WhatsApp, SMS, Enterprise Features)",
    list: [
      "Add-on services such as WhatsApp notification packs, SMS packs and enterprise feature packs are charged separately from the base subscription.",
      "Notification packs and other digital add-ons are non-refundable once used, in full or in part.",
      "Unused add-ons attached to an active subscription remain available until the subscription ends or the pack expires, whichever is earlier.",
    ],
  },
  {
    h: "Cancellation Policy",
    list: [
      "You may cancel your subscription at any time by contacting us using the details in this policy.",
      "Cancellation stops future renewals; it does not terminate the current paid period, which remains active until its end date.",
      "After cancellation, your data is retained or deleted in accordance with the Data Retention terms of our Privacy Policy.",
    ],
  },
  {
    h: "Renewal Policy",
    list: [
      "Subscriptions renew at the end of each billing period unless cancelled before the renewal date.",
      "We may send renewal reminders before the renewal date via in-app notice, email, SMS or WhatsApp.",
      "If a renewal payment is not received, access to paid features may be suspended until payment is made.",
    ],
  },
  {
    h: "Refund Eligibility",
    p: ["A refund may be considered in the following situations, after verification by our team:"],
    list: [
      "Payment was deducted but the subscription was not activated due to a verified technical issue on our side — the issue will be resolved or an appropriate refund may be processed after verification.",
      "A duplicate payment was made for the same subscription period (see Duplicate Payment Handling).",
      "We permanently discontinue the Service during a paid period — the unused portion will be refunded on a pro-rata basis.",
      "A refund is required under applicable law.",
    ],
    after: "Approved refunds are processed to the original payment method, normally within 7–14 business days of approval.",
  },
  {
    h: "Non-Refundable Services",
    p: ["The following are non-refundable:"],
    list: [
      "Subscription fees for a billing period that has already commenced (monthly or annual).",
      "Setup, onboarding, training, customization and implementation charges once delivered.",
      "WhatsApp/SMS notification packs and other digital add-ons once used.",
      "Charges arising from your failure to cancel before a renewal date.",
      "Periods of suspension caused by a breach of our Terms & Conditions.",
    ],
  },
  {
    h: "Failed Payment Handling",
    list: [
      "If a payment fails, no amount is charged and the subscription is not activated or renewed.",
      "If your bank debits an amount for a failed transaction, such amounts are typically auto-reversed by the bank/payment provider within 5–7 business days.",
      "If the amount is not reversed within this period, contact us with the transaction reference and we will assist in tracing it with the payment provider.",
    ],
  },
  {
    h: "Duplicate Payment Handling",
    list: [
      "Duplicate payments made for the same subscription period will be reviewed and refunded where applicable.",
      "To report a duplicate payment, share both transaction references (date, amount, payment ID) with us.",
      "Verified duplicate amounts are refunded to the original payment method, normally within 7–14 business days.",
    ],
  },
  {
    h: "Contact for Refund Requests",
    p: [
      "To request a refund or cancellation, contact us with your registered mobile number, haat name and payment reference:",
    ],
    contact: true,
  },
  {
    h: "Policy Changes",
    p: [
      "We may update this Refund & Cancellation Policy from time to time. The updated version will be posted on this page with a revised effective date. Continued use of the Service after changes take effect constitutes acceptance of the updated policy.",
    ],
  },
];

export default function RefundPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "AnimalMela Refund & Cancellation Policy";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const prevDesc = meta.content;
    meta.content = "Official Refund & Cancellation Policy for AnimalMela operated by RIMITANA GLOBAL NETWORKS PRIVATE LIMITED.";
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else meta.content = prevDesc;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="refund-policy-page">
      {/* Header (hidden on print) */}
      <header className="border-b print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="refund-home-link">
            <BrandLogo size="footer" alt="AnimalMela" />
            <span className="font-display font-extrabold">AnimalMela</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900" data-testid="refund-print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <Link to="/login" className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900" data-testid="refund-back-login">
              <ArrowLeft className="w-4 h-4" /> Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Refund &amp; Cancellation Policy</h1>
        <p className="mt-3 text-base font-semibold">AnimalMela</p>
        <p className="text-sm text-gray-500">A Product of {COMPANY}</p>
        <p className="mt-2 text-sm text-gray-500">Effective Date: <span className="font-semibold text-gray-700">11 August 2026</span></p>

        <div className="mt-10 space-y-10">
          {SECTIONS.map((s, i) => (
            <section key={s.h} data-testid={`refund-section-${i}`}>
              <h2 className="text-lg font-bold mb-3">{i + 1}. {s.h}</h2>
              {(s.p || []).map((para, j) => (
                <p key={j} className="text-[15px] leading-7 text-gray-700 mb-3">{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-6 space-y-2 text-[15px] leading-7 text-gray-700">
                  {s.list.map((li, j) => <li key={j}>{li}</li>)}
                </ul>
              )}
              {s.after && <p className="text-[15px] leading-7 text-gray-700 mt-3">{s.after}</p>}
              {s.contact && (
                <div className="rounded-2xl border p-5 text-[15px] leading-8 mt-1" data-testid="refund-contact-block">
                  <div><span className="font-semibold">Company:</span> {COMPANY}</div>
                  <div><span className="font-semibold">Website:</span> <a href={`https://${SITE}`} className="text-green-700 underline underline-offset-2" target="_blank" rel="noreferrer">{SITE}</a></div>
                  <div><span className="font-semibold">Email:</span> <a href={`mailto:${EMAIL}`} className="text-green-700 underline underline-offset-2">{EMAIL}</a></div>
                  <div><span className="font-semibold">Phone:</span> <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="text-green-700 underline underline-offset-2">{PHONE}</a></div>
                </div>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-6 border-t text-xs text-gray-400">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </footer>
      </main>
    </div>
  );
}
